/**
 * InstitutionQBankPage.js
 * Institution-wide Q.Bank browser — all projects, sorted newest first.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ExamPageShell from '../components/layout/ExamPageShell';
import EmptyState from '../components/common/EmptyState';
import ExamBadge from '../components/common/ExamBadge';
import { listExamProjects } from '../api/examApi';

const STATUS_META = {
  draft:      { label: 'Draft',      status: 'draft'    },
  active:     { label: 'Active',     status: 'ready'    },
  archived:   { label: 'Archived',   status: 'archived' },
  processing: { label: 'Processing', status: 'pending'  },
};

// A project "has" a Q.Bank if it was created as qbank_upload type,
// or if Q.Bank uploads have been started (qbank_status left 'pending' state).
const hasQBank = (p) =>
  p.project_type === 'qbank_upload' ||
  (p.qbank_status && p.qbank_status !== 'pending');

function ProjectQBankCard({ project: p }) {
  const meta = STATUS_META[p.status] || { label: p.status, status: 'draft' };
  const qbank = hasQBank(p);

  return (
    <div className="exam-card" style={{ padding: '16px 20px', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
      <div style={{
        width: 40, height: 40, borderRadius: 8, flexShrink: 0,
        background: 'var(--exam-primary-light)', color: 'var(--exam-primary)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
      }}>
        🗂️
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--exam-text)' }}>{p.title}</span>
          <ExamBadge label={meta.label} status={meta.status} />
          {qbank && <ExamBadge label="Q.Bank" status="ready" />}
        </div>
        <p style={{ fontSize: 12, color: 'var(--exam-text-muted)', margin: 0, lineHeight: 1.5 }}>
          {p.course_name || p.institution_name || '—'}
          {p.course_code ? ` · ${p.course_code}` : ''}
          {p.semester ? ` · ${p.semester}` : ''}
        </p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
        <Link
          to={`/examination/projects/${p.id}/qbank-mapping`}
          className="exam-btn exam-btn-primary exam-btn-sm"
          style={{ textDecoration: 'none', textAlign: 'center' }}
        >
          Review Q.Bank
        </Link>
        <Link
          to={`/examination/projects/${p.id}/qbank-upload`}
          className="exam-btn exam-btn-ghost exam-btn-sm"
          style={{ textDecoration: 'none', textAlign: 'center' }}
        >
          Upload
        </Link>
      </div>
    </div>
  );
}

export default function InstitutionQBankPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    listExamProjects()
      .then((data) => {
        const list = Array.isArray(data) ? data : (data.projects || []);
        // Newest created first
        list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setProjects(list);
      })
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = projects.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      p.title?.toLowerCase().includes(q) ||
      p.course_name?.toLowerCase().includes(q) ||
      p.course_code?.toLowerCase().includes(q)
    );
  });

  const qbankCount = projects.filter(hasQBank).length;

  return (
    <ExamPageShell breadcrumbs={[{ label: 'Question Bank' }]}>
      <div style={{ maxWidth: 920, margin: '0 auto' }}>
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--exam-text)' }}>Institution Question Bank</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--exam-text-muted)' }}>
            Browse Q.Bank uploads across all projects. Click a project to review or upload questions.
          </p>
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { label: 'Total Projects', value: projects.length },
            { label: 'With Q.Bank',    value: qbankCount, color: 'var(--exam-success)' },
          ].map((s) => (
            <div key={s.label} className="exam-card" style={{ padding: '12px 20px', textAlign: 'center', minWidth: 120 }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: s.color || 'var(--exam-primary)' }}>{s.value}</div>
              <div style={{ fontSize: 11, color: 'var(--exam-text-muted)', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Search */}
        <div style={{ marginBottom: 16 }}>
          <input
            className="exam-input"
            placeholder="Search by project name or course…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 360 }}
          />
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <div className="exam-spinner" style={{ width: 28, height: 28, borderWidth: 3 }} />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState icon="🗂️" title="No projects found" description={search ? 'Try a different search term.' : 'Create a project to get started.'} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filtered.map((p) => <ProjectQBankCard key={p.id} project={p} />)}
          </div>
        )}
      </div>
    </ExamPageShell>
  );
}
